import type { ReactNode } from "react";
import { Pressable, ScrollView, Text, View } from "react-native";
import type { DayPanelEntry } from "@heraion/shared";

import type { ComposerStyles } from "./composerStyles";
import type { PlanStyles } from "./planComposerStyles";
import { ComposerEntryRow } from "./ComposerEntryRow";
import { EditorTopBar } from "../primitives/EditorTopBar";
import type { UiTheme } from "../theme";

// The edit-in-place shell shared by the single Plan + program composers:
// the top bar (the shared <EditorTopBar> primitive — ‹ back · title · Save +
// its error line), an optional warnings line, and the two-breakpoint body —
// narrow = an accordion (the selected row IS replaced by the inline editor),
// wide = master-detail (list pane | selected-exercise editor pane). One tree,
// layout by `wide` ([[feedback_lift_mirrors_mobile]]).
//
// Slots the host fills: `metaBand` (mode-specific meta header), `editor` (the
// expanded exercise editor), and `picker` (the fully-built host Picker
// element). Row rendering + selection/expansion state is owned here via
// ComposerEntryRow.
export function ComposerEditLayout({
  cs,
  ps,
  theme,
  wide,
  busy,
  backLabel,
  title,
  onCancel,
  onSave,
  error,
  status,
  warnings,
  metaBand,
  sectionLabel,
  emptyHint,
  selectPrompt,
  entries,
  resolveName,
  editingIdx,
  editing,
  composingNew,
  hasForm,
  editor,
  onOpenEntry,
  onOpenAdd,
  addLabel,
  picker,
}: {
  cs: ComposerStyles;
  ps: PlanStyles;
  theme: UiTheme;
  wide: boolean;
  busy: boolean;
  backLabel: string;
  title: string;
  onCancel: () => void;
  onSave: () => void;
  error: string | null;
  /** #336: the save line under the bar (EditorSaveStatus). */
  status?: ReactNode;
  warnings?: ReactNode;
  metaBand: ReactNode;
  sectionLabel: string;
  emptyHint: string;
  selectPrompt: string;
  entries: DayPanelEntry[];
  resolveName: (exerciseId: string, snapshot?: string) => string;
  editingIdx: number | null;
  editing: boolean;
  composingNew: boolean;
  hasForm: boolean;
  editor: ReactNode;
  onOpenEntry: (idx: number) => void;
  onOpenAdd: () => void;
  addLabel: string;
  picker: ReactNode;
}) {
  const row = (entry: DayPanelEntry, idx: number) => (
    <ComposerEntryRow
      key={idx}
      entry={entry}
      idx={idx}
      selected={wide && editing && editingIdx === idx}
      expanded={editing && editingIdx === idx}
      onPress={onOpenEntry}
      resolveName={resolveName}
      ps={ps}
    />
  );

  const addBtn = (
    <Pressable accessibilityRole="button" onPress={onOpenAdd} style={cs.addExercisesBtn}>
      <Text style={cs.addExercisesText}>＋ {addLabel}</Text>
    </Pressable>
  );

  // The empty-list hint is suppressed while composing a NEW exercise only on
  // narrow (where that editor appends to this same list); on wide it lives in
  // the detail pane, so the list-pane hint stays.
  const listHead = (hideWhenComposing: boolean) => (
    <>
      <Text style={ps.sectionLabel}>{sectionLabel}</Text>
      {entries.length === 0 && !(hideWhenComposing && composingNew) ? (
        <Text style={ps.listHint}>{emptyHint}</Text>
      ) : null}
    </>
  );

  // The editor always has a home: it REPLACES its row when editingIdx points
  // at a real entry, else it trails the list (a brand-new compose, or an
  // editing form whose row index went stale — e.g. a day switch that left an
  // un-committable conflict — so it can never become an invisible zombie).
  const editorHasHomeRow =
    editing && editingIdx !== null && editingIdx >= 0 && editingIdx < entries.length;

  // Narrow accordion: the selected entry's row is REPLACED by the inline
  // editor; an orphan/new compose appends its editor after the list.
  const narrowList = (
    <>
      {listHead(true)}
      {entries.map((entry, idx) =>
        idx === editingIdx && editing ? (
          <View key={`e${idx}`}>{editor}</View>
        ) : (
          row(entry, idx)
        ),
      )}
      {composingNew || (editing && !editorHasHomeRow) ? (
        <View key="new">{editor}</View>
      ) : null}
      {addBtn}
    </>
  );

  const detailPane = (
    <View style={ps.detailPane}>
      {hasForm ? (
        <ScrollView contentContainerStyle={ps.detailContent} keyboardShouldPersistTaps="handled">
          {/* Key by entry so switching exercises REMOUNTS the editor — its
              local state (e.g. the two-step ✕ remove confirm) must not leak
              from one exercise to the next in the persistent detail pane. */}
          <View key={editingIdx ?? "new"}>{editor}</View>
        </ScrollView>
      ) : (
        <View style={ps.emptyPane}>
          <Text style={ps.emptyGlyph}>‹</Text>
          <Text style={ps.emptyTitle}>
            {entries.length === 0 ? emptyHint : selectPrompt}
          </Text>
        </View>
      )}
    </View>
  );

  return (
    <View style={cs.container}>
      <EditorTopBar
        theme={theme}
        backLabel={backLabel}
        onBack={onCancel}
        title={title}
        onSave={onSave}
        busy={busy}
        error={error}
        status={status}
      />
      {warnings}

      {wide ? (
        <View style={{ flex: 1 }}>
          <View style={{ padding: 16 }}>{metaBand}</View>
          <View style={ps.masterRow}>
            {/* Fixed-width pane = View wrapping the ScrollView; a fixed-width
                ScrollView mis-sizes on RNW ([[reference_rnw_fixed_width_pane_not_scrollview]]). */}
            <View style={ps.listPane}>
              <ScrollView
                contentContainerStyle={ps.listContent}
                keyboardShouldPersistTaps="handled"
              >
                {listHead(false)}
                {entries.map((entry, idx) => row(entry, idx))}
                {addBtn}
              </ScrollView>
            </View>
            {detailPane}
          </View>
        </View>
      ) : (
        <ScrollView
          style={cs.scroll}
          contentContainerStyle={ps.listContent}
          keyboardShouldPersistTaps="handled"
        >
          {metaBand}
          {narrowList}
        </ScrollView>
      )}

      {picker}
    </View>
  );
}
